/**
 * Realtime hook — single multiplexed WebSocket per session with exponential
 * reconnect. Events invalidate the right React Query caches so the UI is
 * live everywhere (job progress, credit balance, registry publishes)
 * without any polling.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { JobDto, WsEnvelope } from '@vidply/shared';

export function useRealtime(enabled: boolean): void {
  const queryClient = useQueryClient();
  const retryRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let socket: WebSocket | null = null;
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      socket = new WebSocket(`${proto}://${location.host}/api/v1/ws`);

      socket.onopen = () => {
        retryRef.current = 0;
      };

      socket.onmessage = (msg) => {
        let env: WsEnvelope;
        try {
          env = JSON.parse(msg.data as string) as WsEnvelope;
        } catch {
          return;
        }
        const e = env.event;
        switch (e.type) {
          case 'graph.update': {
            // Live DAG progress for wizards/studios; project pages join in.
            queryClient.setQueryData(['graph', e.graph.id], { graph: e.graph });
            if (e.graph.state !== 'RUNNING') {
              void queryClient.invalidateQueries({ queryKey: ['ugc'] });
              void queryClient.invalidateQueries({ queryKey: ['assets'] });
            }
            break;
          }
          case 'job.update': {
            // Push the fresh job straight into the cache — no refetch.
            queryClient.setQueryData(['job', e.job.id], { job: e.job });
            queryClient.setQueryData<{ jobs: JobDto[] } | undefined>(['jobs'], (old) =>
              old ? { jobs: old.jobs.map((j) => (j.id === e.job.id ? e.job : j)) } : old,
            );
            if (e.job.state === 'SUCCEEDED') {
              void queryClient.invalidateQueries({ queryKey: ['assets'] });
              void queryClient.invalidateQueries({ queryKey: ['jobs'] });
            }
            break;
          }
          case 'credits.update':
            queryClient.setQueryData(['credits'], (old: { grants?: unknown[] } | undefined) => ({
              balances: e.balances,
              grants: old && 'grants' in old ? old.grants : [],
            }));
            break;
          case 'manifest.published':
            void queryClient.invalidateQueries({ queryKey: ['models'] });
            break;
          case 'promo.update':
            void queryClient.invalidateQueries({ queryKey: ['promotions'] });
            break;
          case 'notification':
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
            break;
        }
      };

      socket.onclose = () => {
        if (closed) return;
        // Exponential backoff capped at 15s; UI keeps working via queries.
        const delay = Math.min(15_000, 1000 * 2 ** retryRef.current++);
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [enabled, queryClient]);
}
